/**
 * Data Scope Viewer Component
 *
 * Displays accessible locations/orgUnits with hierarchical view
 * Updated for v4 DataScope structure
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  LocationOn,
  Business,
  Search,
  Warning,
  CheckCircle,
  Public,
  Language,
  AccountTree
} from '@mui/icons-material';
import { isScopeUniversal } from '../../../../../../utils/permissionHelpers';

export default function DataScopeViewer({ dataScope = {}, user }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'locations', 'orgUnits'

  // Determine scope type
  const scopeType = dataScope.scopeType || 'ASSIGNMENT_SCOPED';
  const isUniversal = isScopeUniversal(scopeType);

  // Get accessible entities
  const accessibleLocations = dataScope.accessibleLocations || [];
  const accessibleOrgUnits = dataScope.accessibleOrgUnits || [];

  // Filter entities based on search
  const filteredLocations = useMemo(() => {
    if (!searchTerm) return accessibleLocations;
    return accessibleLocations.filter(loc =>
      loc.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [accessibleLocations, searchTerm]);

  const filteredOrgUnits = useMemo(() => {
    if (!searchTerm) return accessibleOrgUnits;
    return accessibleOrgUnits.filter(ou =>
      ou.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [accessibleOrgUnits, searchTerm]);

  // Calculate statistics
  const stats = useMemo(() => {
    return {
      totalLocations: accessibleLocations.length,
      totalOrgUnits: accessibleOrgUnits.length,
      scopeType: scopeType
    };
  }, [accessibleLocations.length, accessibleOrgUnits.length, scopeType]);

  // Render scope type badge
  const getScopeTypeBadge = () => {
    const badges = {
      UNIVERSAL: {
        icon: <Public />,
        label: 'Universal Access',
        color: 'warning',
        description: 'Access to all locations and entities in the system'
      },
      CLIENT_SCOPED: {
        icon: <Language />,
        label: 'Client-Wide Access',
        color: 'info',
        description: 'Access scoped to entire client organization'
      },
      ASSIGNMENT_SCOPED: {
        icon: <AccountTree />,
        label: 'Assignment-Based Access',
        color: 'primary',
        description: 'Access limited to specific assigned locations/orgUnits'
      }
    };

    const badge = badges[scopeType] || badges.ASSIGNMENT_SCOPED;

    return (
      <Card sx={{ mb: 2, bgcolor: `${badge.color}.light` }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            {badge.icon}
            <Box>
              <Typography variant="h6">{badge.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {badge.description}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Render hierarchy flags
  const getHierarchyFlags = () => {
    const flags = [];

    if (dataScope.includeChildren) {
      flags.push(
        <Chip
          key="children"
          label="Include Children"
          size="small"
          color="info"
          variant="outlined"
        />
      );
    }

    if (dataScope.includePeers) {
      flags.push(
        <Chip
          key="peers"
          label="Include Peers"
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    }

    if (dataScope.includeParent) {
      flags.push(
        <Chip
          key="parent"
          label="Include Parent"
          size="small"
          color="secondary"
          variant="outlined"
        />
      );
    }

    return flags;
  };

  const renderLocationsList = () => {
    if (filteredLocations.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? 'No locations match the search' : 'No accessible locations'}
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {filteredLocations.map((location, index) => (
          <React.Fragment key={location}>
            <ListItem>
              <ListItemIcon>
                <LocationOn color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={location}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            {index < filteredLocations.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  const renderOrgUnitsList = () => {
    if (filteredOrgUnits.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? 'No org units match the search' : 'No accessible org units'}
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {filteredOrgUnits.map((orgUnit, index) => (
          <React.Fragment key={orgUnit}>
            <ListItem>
              <ListItemIcon>
                <Business color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary={orgUnit}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            {index < filteredOrgUnits.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Box>
      {/* Scope Type Badge */}
      {getScopeTypeBadge()}

      {/* Universal Access Alert */}
      {isUniversal && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Full Data Access</Typography>
          <Typography variant="body2">
            This user has UNIVERSAL access to all locations and entities in the system.
            No specific location restrictions apply.
          </Typography>
        </Alert>
      )}

      {/* Hierarchy Expansion Flags */}
      {(dataScope.includeChildren || dataScope.includePeers || dataScope.includeParent) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Hierarchy Expansion
          </Typography>
          <Stack direction="row" spacing={1}>
            {getHierarchyFlags()}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            These locations/orgUnits include hierarchical relationships
          </Typography>
        </Paper>
      )}

      {/* Statistics */}
      {!isUniversal && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocationOn color="primary" />
                <Box>
                  <Typography variant="h6">{stats.totalLocations}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accessible Locations
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Business color="secondary" />
                <Box>
                  <Typography variant="h6">{stats.totalOrgUnits}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accessible Org Units
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Search and Filters */}
      {!isUniversal && (accessibleLocations.length > 0 || accessibleOrgUnits.length > 0) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              placeholder="Search locations or org units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
              fullWidth
            />

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={filterType === 'all' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('all')}
              >
                All
              </Button>
              <Button
                size="small"
                variant={filterType === 'locations' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('locations')}
                disabled={accessibleLocations.length === 0}
              >
                Locations ({stats.totalLocations})
              </Button>
              <Button
                size="small"
                variant={filterType === 'orgUnits' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('orgUnits')}
                disabled={accessibleOrgUnits.length === 0}
              >
                Org Units ({stats.totalOrgUnits})
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Data Scope Lists */}
      {!isUniversal && (
        <>
          {(filterType === 'all' || filterType === 'locations') && accessibleLocations.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ p: 2, bgcolor: 'primary.light' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOn />
                  <Typography variant="subtitle1">Accessible Locations</Typography>
                  <Chip label={filteredLocations.length} size="small" variant="outlined" />
                </Stack>
              </Box>
              {renderLocationsList()}
            </Paper>
          )}

          {(filterType === 'all' || filterType === 'orgUnits') && accessibleOrgUnits.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ p: 2, bgcolor: 'secondary.light' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Business />
                  <Typography variant="subtitle1">Accessible Org Units</Typography>
                  <Chip label={filteredOrgUnits.length} size="small" variant="outlined" />
                </Stack>
              </Box>
              {renderOrgUnitsList()}
            </Paper>
          )}
        </>
      )}

      {/* Info Box */}
      <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <CheckCircle />
          <Box>
            <Typography variant="subtitle2">Data Scope Information</Typography>
            <Typography variant="body2" component="div">
              <strong>Scope Type:</strong> {scopeType}
              <br />
              <strong>Description:</strong>
              <br />
              • <strong>UNIVERSAL:</strong> Access to all locations and entities in the system
              <br />
              • <strong>CLIENT_SCOPED:</strong> Access scoped to the entire client organization
              <br />
              • <strong>ASSIGNMENT_SCOPED:</strong> Access limited to specific assigned locations/orgUnits
              <br />
              <br />
              <strong>Hierarchy Expansion:</strong>
              <br />
              • <strong>Include Children:</strong> Access extends to all subordinate entities
              <br />
              • <strong>Include Peers:</strong> Access extends to sibling entities at the same level
              <br />
              • <strong>Include Parent:</strong> Access extends to parent entities
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
